import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import DetailsCard, { type SelectOptions } from "@/components/DetailsCard/DetailsCard";
import ListItem from "@/components/ListItem";
import { CardSkeleton } from "@/components/Skeleton";
import { formatDate } from "@/pages/_layouts/jobs/_utils";

export default function CareerPlanSummaryCard({
  title,
  testId,
  updatedAt,
  loading,
  viewAllHref,
  showViewAll = true,
  options = [],
  children
}: {
  title: string;
  testId: string;
  updatedAt?: string | null;
  loading: boolean;
  viewAllHref?: string;
  showViewAll?: boolean;
  options?: SelectOptions[];
  children?: ReactNode;
}) {
  const updatedLabel = updatedAt ? `Updated ${formatDate(updatedAt)}` : null;

  return (
    <div className="h-full min-w-0 w-full" data-testid={testId}>
      {loading ? (
        <CardSkeleton className="w-full" width={100} rows={5} />
      ) : (
        <DetailsCard
          heading={title}
          className="!items-stretch"
          actionButtons={
            showViewAll && viewAllHref ? (
              <div
                className="-mx-6 -mb-6 mt-auto flex w-[calc(100%+3rem)] items-center justify-center border-t border-sys-brand-surface-container-highest py-6"
                data-testid={`${testId}-view-footer`}
              >
                <Link
                  to={viewAllHref}
                  data-testid={`${testId}-view-all`}
                  aria-label={`View ${title}`}
                  className="inline-flex min-h-10 items-center justify-center text-label-large text-sys-brand-primary"
                >
                  View
                </Link>
              </div>
            ) : undefined
          }
        >
          <div className="flex min-h-0 w-full flex-1 flex-col">
            {updatedLabel ? (
              <p
                className="-mt-1 mb-4 text-body-medium text-sys-brand-on-surface-variant"
                data-testid="details-card-caption"
              >
                {updatedLabel}
              </p>
            ) : null}
            {options.length > 0 ? (
              <div className="flex w-full min-w-0 max-w-full flex-col">
                {options.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <ListItem {...item} iconBackground />
                  </div>
                ))}
              </div>
            ) : null}
            {children}
          </div>
        </DetailsCard>
      )}
    </div>
  );
}
